import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Proposal, VoteType, getStateString, formatTimestamp, formatVotes, formatVotesCompact } from '../contracts/contract';
import { ellipsizeCenter } from '../utils/stringUtils';
import { useWallet } from '../contexts/WalletContext';
import { useNetwork } from '../contexts/NetworkContext';
import { useGovernance } from '../contexts/GovernanceContext';
import { trustlineService } from '../services/trustline';
import DescriptionModal from './DescriptionModal';
import './VotingPanel.css';

interface VotingPanelProps {
  proposal: Proposal;
  onVote: (proposalId: string, support: VoteType, reason?: string) => void;
  onClose: () => void;
  loading: boolean;
}

const VotingPanel: React.FC<VotingPanelProps> = ({ proposal, onVote, onClose, loading }) => {
  const { account } = useWallet();
  const { currentNetwork } = useNetwork();
  const { contractAddress } = useGovernance();
  const [selectedVote, setSelectedVote] = useState<VoteType | null>(null);
  const [reason, setReason] = useState('');
  const [showVoteForm, setShowVoteForm] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  const handleVote = async () => {
    if (selectedVote !== null) {
      // Clear any previous errors
      setError('');
      
      // Trustline validation for vote
      if (currentNetwork && account && contractAddress) {
        try {
          const isApproved = await trustlineService.validateVote(
            currentNetwork.chainId,
            account,
            contractAddress,
            proposal.id,
            selectedVote,
            reason.trim() || undefined
          );

          if (!isApproved) {
            throw new Error('Vote transaction not approved by Trustline compliance check');
          }
       
          onVote(proposal.id, selectedVote, reason.trim() || undefined);
        } catch (err) {
          console.error('Trustline validation failed:', err);
          setError(err instanceof Error ? err.message : 'Trustline validation failed');
        }
      } else {
        // If no network/account/contract, proceed without validation
        onVote(proposal.id, selectedVote, reason.trim() || undefined);
      }
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(proposal.id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy proposal ID:', err);
    }
  };

  const handleVoteChange = (vote: VoteType) => {
    setSelectedVote(vote);
    setError(''); // Clear error when vote changes
  };

  const handleReasonChange = (newReason: string) => {
    setReason(newReason);
    setError(''); // Clear error when reason changes
  };

  const getVotePercentage = (votes: string, total: number): number => {
    if (total === 0) return 0;
    return (Number(votes) / total) * 100;
  };

  const totalVotes = Number(proposal.forVotes) + Number(proposal.againstVotes) + Number(proposal.abstainVotes);
  const forPercentage = getVotePercentage(proposal.forVotes, totalVotes);
  const againstPercentage = getVotePercentage(proposal.againstVotes, totalVotes);
  const abstainPercentage = getVotePercentage(proposal.abstainVotes, totalVotes);

  const canVote = proposal.state === 1; // Active state

  return (
    <div className="voting-panel-overlay">
      <div className="voting-panel-modal">
        <div className="modal-header">
          <div className="proposal-id-container">
            <h2>Proposal #{ellipsizeCenter(proposal.id, 12)}</h2>
            <button 
              className="copy-btn" 
              onClick={copyToClipboard}
              title={copySuccess ? "Copied!" : "Copy proposal ID"}
              disabled={loading}
            >
              {copySuccess ? "✓" : "📋"}
            </button>
          </div>
          <button className="close-btn" onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>

        <div className="voting-panel-content">
          <div className="proposal-details">
            <div className="proposal-status">
              <span className={`status-badge ${getStateString(proposal.state).toLowerCase()}`}>
                {getStateString(proposal.state)}
              </span>
            </div>

            <div className="proposal-description">
              <h3>Description</h3>
              {proposal.description ? (
                <div 
                  className="description-preview"
                  onClick={() => setIsDescriptionModalOpen(true)}
                  title="Click to view full description"
                >
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {proposal.description}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <p>No description available</p>
              )}
            </div>

            <div className="proposal-actions">
              <h3>Actions</h3>
              <div className="actions-list">
                {proposal.targets.map((target, index) => (
                  <div key={index} className="action-item">
                    <div className="action-target">
                      <strong>Target:</strong> {target}
                    </div>
                    <div className="action-value">
                      <strong>Value:</strong> {formatVotes(proposal.values[index])} ETH
                    </div>
                    {proposal.calldatas[index] && (
                      <div className="action-calldata">
                        <strong>Calldata:</strong> {proposal.calldatas[index]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="proposal-timeline">
              <h3>Timeline</h3>
              <div className="timeline-item">
                <strong>Vote Start:</strong> {formatTimestamp(proposal.voteStart)}
              </div>
              <div className="timeline-item">
                <strong>Vote End:</strong> {formatTimestamp(proposal.voteEnd)}
              </div>
            </div>
          </div>

          <div className="voting-section">
            <h3>Voting Results</h3>
            <div className="vote-results">
              <div className="vote-bar">
                <div className="vote-label">
                  <span>For</span>
                  <span>{formatVotesCompact(proposal.forVotes)} ({forPercentage.toFixed(1)}%)</span>
                </div>
                <div className="vote-progress">
                  <div 
                    className="vote-fill for" 
                    style={{ width: `${forPercentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="vote-bar">
                <div className="vote-label">
                  <span>Against</span>
                  <span>{formatVotesCompact(proposal.againstVotes)} ({againstPercentage.toFixed(1)}%)</span>
                </div>
                <div className="vote-progress">
                  <div 
                    className="vote-fill against" 
                    style={{ width: `${againstPercentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="vote-bar">
                <div className="vote-label">
                  <span>Abstain</span>
                  <span>{formatVotesCompact(proposal.abstainVotes)} ({abstainPercentage.toFixed(1)}%)</span>
                </div>
                <div className="vote-progress">
                  <div 
                    className="vote-fill abstain" 
                    style={{ width: `${abstainPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {canVote && (
              <div className="vote-form">
                <h3>Cast Your Vote</h3>
                
                {!showVoteForm ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowVoteForm(true)}
                    disabled={loading}
                  >
                    Vote on This Proposal
                  </button>
                ) : (
                  <div className="vote-options">
                    <div className="vote-option">
                      <input
                        type="radio"
                        id="vote-for"
                        name="vote"
                        value={VoteType.For}
                        onChange={(e) => handleVoteChange(Number(e.target.value) as VoteType)}
                        disabled={loading}
                      />
                      <label htmlFor="vote-for">For</label>
                    </div>
                    <div className="vote-option">
                      <input
                        type="radio"
                        id="vote-against"
                        name="vote"
                        value={VoteType.Against}
                        onChange={(e) => handleVoteChange(Number(e.target.value) as VoteType)}
                        disabled={loading}
                      />
                      <label htmlFor="vote-against">Against</label>
                    </div>
                    <div className="vote-option">
                      <input
                        type="radio"
                        id="vote-abstain"
                        name="vote"
                        value={VoteType.Abstain}
                        onChange={(e) => handleVoteChange(Number(e.target.value) as VoteType)}
                        disabled={loading}
                      />
                      <label htmlFor="vote-abstain">Abstain</label>
                    </div>

                    <div className="vote-reason">
                      <label htmlFor="reason">Reason (optional)</label>
                      <textarea
                        id="reason"
                        value={reason}
                        onChange={(e) => handleReasonChange(e.target.value)}
                        placeholder="Explain your vote..."
                        rows={3}
                        disabled={loading}
                      />
                    </div>

                    {error && (
                      <div className="error">
                        {error}
                      </div>
                    )}

                    <div className="vote-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowVoteForm(false);
                          setSelectedVote(null);
                          setReason('');
                          setError('');
                        }}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleVote}
                        disabled={loading || selectedVote === null}
                      >
                        {loading ? 'Submitting...' : 'Submit Vote'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!canVote && (
              <div className="vote-disabled">
                <p>This proposal is not currently active and cannot be voted on.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <DescriptionModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        title={`Proposal #${ellipsizeCenter(proposal.id, 12)} - Description`}
        description={proposal.description || 'No description available'}
      />
    </div>
  );
};

export default VotingPanel;
